import React from "react";
import { Box, Skeleton, Paper, Stack, Divider } from "@mui/material";
import PageContainer from "./PageContainer";
import PageSection from "./PageSection";

const ViewNoticesSkeleton: React.FC = () => {
  // Create more realistic variations for each card
  const getCardVariation = (index: number) => {
    const variations = [
      {
        titleWidth: "65%",
        bodyLines: [100, 90, 75],
        hasAttachment: false,
        hasViews: true,
      },
      {
        titleWidth: "45%",
        bodyLines: [100, 80],
        hasAttachment: true,
        hasViews: false,
      },
      {
        titleWidth: "70%",
        bodyLines: [100, 95, 85, 60],
        hasAttachment: false,
        hasViews: false,
      },
      {
        titleWidth: "55%",
        bodyLines: [100, 70, 85],
        hasAttachment: true,
        hasViews: true,
      },
      {
        titleWidth: "80%",
        bodyLines: [100, 88],
        hasAttachment: false,
        hasViews: false,
      },
      {
        titleWidth: "60%",
        bodyLines: [100, 92, 70, 80],
        hasAttachment: false,
        hasViews: true,
      },
    ];
    return variations[index % variations.length];
  };

  return (
    <PageContainer sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
      {/* Search Section Skeleton */}
      <Box sx={{ mb: 3 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={48}
          sx={{ borderRadius: 2 }}
        />
      </Box>

      {/* Filter Section Skeleton */}
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ flexWrap: "wrap", gap: 1, mb: 1 }}
        >
          <Skeleton
            variant="rectangular"
            width={100}
            height={36}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={120}
            height={36}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
      </Box>

      {/* Notice Cards Skeleton */}
      <PageSection>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Array.from({ length: 6 }).map((_, index) => {
            const variation = getCardVariation(index);

            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 1,
                    borderRadius: 2,
                    boxShadow: "none",
                    bgcolor: "background.paper",
                  }}
                >
                  {/* Title and badges row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.25,
                      gap: 1,
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width={variation.titleWidth}
                      height={24}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton
                        variant="rectangular"
                        width={index % 3 === 0 ? 50 : index % 3 === 1 ? 65 : 55}
                        height={24}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </Box>

                  {/* Sender and timestamp row with better spacing */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    mb={1}
                    divider={
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ opacity: 0.1 }}
                      />
                    }
                    sx={{
                      color: "text.secondary",
                      fontSize: 13,
                      alignItems: "center",
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Skeleton variant="circular" width={16} height={16} />
                      <Skeleton
                        variant="text"
                        width={index % 2 === 0 ? 120 : 90}
                        height={16}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Skeleton variant="circular" width={16} height={16} />
                      <Skeleton
                        variant="text"
                        width={index % 3 === 0 ? 140 : 110}
                        height={16}
                      />
                    </Stack>
                  </Stack>

                  {/* Body content with variable lines */}
                  <Box sx={{ mb: 0.5 }}>
                    {variation.bodyLines.map((width, lineIndex) => (
                      <Skeleton
                        key={lineIndex}
                        variant="text"
                        width={`${width}%`}
                        height={16}
                        sx={{ mt: lineIndex > 0 ? 0.25 : 0 }}
                      />
                    ))}
                  </Box>

                  {/* Attachments */}
                  {variation.hasAttachment && (
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton
                        variant="text"
                        width={90}
                        height={16}
                        sx={{ mb: 0.25 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                        }}
                      >
                        <Skeleton variant="text" width={180} height={14} />
                        {index % 4 === 0 && (
                          <Skeleton variant="text" width={160} height={14} />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* View count with progress bar */}
                  {variation.hasViews && (
                    <Box
                      sx={{
                        mt: 1,
                        pt: 0.5,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.5,
                        }}
                      >
                        <Skeleton variant="text" width={40} height={14} />
                        <Skeleton
                          variant="text"
                          width={index % 2 === 0 ? 85 : 95}
                          height={14}
                        />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={3}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Paper>
              </Paper>
            );
          })}
        </Box>
      </PageSection>
    </PageContainer>
  );
};

export default ViewNoticesSkeleton;
